import { Controller } from "@hotwired/stimulus"

// Finestra modale (Radix Dialog / AlertDialog). Si apre con l'evento "dialog:open" (detail.id) inviato da
// dialog-opener, oppure con l'azione dialog#open; si chiude con Esc, clic sull'overlay o dialog#close.
// Gli stati data-state=open|closed attivano le stesse animazioni di tailwindcss-animate dell'originale.
//
// Lo scroll della pagina (react-remove-scroll di Radix: overflow nascosto sul body) resta bloccato finché almeno una
// finestra è aperta: le finestre aperte stanno in un insieme comune, e lo sblocco arriva solo con la chiusura (o
// l'uscita dalla pagina) dell'ultima. Un refresh con morph o un render di Turbo riscrive gli attributi del body e
// toglie il blocco: dopo turbo:morph e turbo:render il blocco si ricalcola dalle finestre ancora aperte (una finestra
// che il morph ha riportato chiusa esce dall'insieme).
const aperte = new Set()

function sincronizzaScroll() {
  document.body.style.overflow = aperte.size > 0 ? "hidden" : ""
}

function dopoIlRender() {
  aperte.forEach((c) => {
    const el = c.element
    if (!el.isConnected || el.hidden || el.dataset.state !== "open") aperte.delete(c)
  })
  sincronizzaScroll()
}

document.addEventListener("turbo:morph", dopoIlRender)
document.addEventListener("turbo:render", dopoIlRender)

export default class extends Controller {
  static targets = ["overlay", "content"]
  static values = { id: String, open: Boolean, dismissable: { type: Boolean, default: true } }

  connect() {
    this.onKey = this.onKey.bind(this)
    this.onExternal = this.onExternal.bind(this)
    window.addEventListener("dialog:open", this.onExternal)
    window.addEventListener("dialog:close", this.onExternal)
    this.closeNow = this.closeNow.bind(this)
    document.addEventListener("turbo:before-cache", this.closeNow)
    if (this.openValue) this.open()
    // una finestra aperta spostata nella pagina (elemento permanente di un render di Turbo) torna nell'insieme
    else if (!this.element.hidden && this.element.dataset.state === "open") this.blocca()
  }

  disconnect() {
    window.removeEventListener("dialog:open", this.onExternal)
    window.removeEventListener("dialog:close", this.onExternal)
    document.removeEventListener("turbo:before-cache", this.closeNow)
    document.removeEventListener("keydown", this.onKey)
    this.sblocca()
  }

  onExternal(event) {
    if (!event.detail || event.detail.id !== this.idValue) return
    event.type === "dialog:open" ? this.open() : this.close()
  }

  open() {
    this.element.hidden = false
    this.setState("open")
    this.blocca()
    document.addEventListener("keydown", this.onKey)
    const focusable = this.contentTarget.querySelector("[autofocus], input:not([type=hidden]), textarea, select, button")
    if (focusable) setTimeout(() => focusable.focus(), 0)
    this.dispatch("opened")
  }

  close() {
    if (this.element.hidden) return
    this.setState("closed")
    document.removeEventListener("keydown", this.onKey)
    this.sblocca()
    setTimeout(() => { this.element.hidden = true }, 200)
    this.dispatch("closed")
  }

  // Prima che Turbo salvi la pagina in cache: chiusa subito, senza animazione, così tornando indietro non si riapre.
  closeNow() {
    if (this.element.hidden) return
    this.setState("closed")
    document.removeEventListener("keydown", this.onKey)
    this.sblocca()
    this.element.hidden = true
  }

  overlayClick() {
    if (this.dismissableValue) this.close()
  }

  // Chiude dopo un invio riuscito di un form Turbo dentro la finestra (data-action="turbo:submit-end->dialog#submitEnd").
  submitEnd(event) {
    if (event.detail.success && !event.target.hasAttribute("data-keep-open")) this.close()
  }

  onKey(event) {
    if (event.key === "Escape" && this.dismissableValue) this.close()
  }

  setState(state) {
    ;[this.element, ...this.overlayTargets, ...this.contentTargets].forEach((el) => (el.dataset.state = state))
  }

  blocca() {
    aperte.add(this)
    sincronizzaScroll()
  }

  // solo una finestra che era aperta toglie il blocco, e solo se era l'ultima
  sblocca() {
    if (aperte.delete(this)) sincronizzaScroll()
  }
}
